import { EMPTY_OBJ } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  ContentType,
  DestinatioType,
  IContentCalloutTemplate,
  IContentDividerTemplate,
  IContentHeadingTemplate,
  IContentImageTemplate,
  IContentTextTemplate,
  ISubTaskBackgroundCheckInviteTemplate,
  ISubTaskCompleteLmsModuleTemplate,
  ISubTaskCalendlyInviteTemplate,
  ISubTaskConfirmTemplate,
  ISubtaskCreateMeetingTemplate,
  ISubtaskDataFieldChangeTemplate,
  ISubTaskDocumentReadTemplate,
  ISubTaskDocuSignEnvelopeSignTemplate,
  ISubTaskQuestionTemplate,
  ISubTaskSubmitTemplate,
  ISubTaskSwagUpChoiceTemplate,
  ISubTaskVisitTemplate,
  ISubTaskWatchTemplate,
  ITaskTemplate,
  LayoutBlock,
  LayoutBlockType,
  SubTaskDataDestination,
  SubTaskDataDestinationBambooHr,
  SubTaskDataDestinationExcel,
  SubTaskDataDestinationGoogleSheet,
  SubTaskDataDestinationUserProfile,
  SubTaskTemplate,
  SubTaskType,
  TaskBlockTemplate
} from '@dadohr/shared/domainTypes/experience';
import {
  isScaleQuestion,
  SCALE_ENDPOINT_LABEL_MAX_LENGTH,
  validateScaleQuestion
} from '@dadohr/shared/domainTypes/questionScale';
import assertNever from 'assert-never';
import { validate as isValidEmail } from 'email-validator';
import { compact, flatten, omit } from 'lodash';
import { SetStateAction, useCallback } from 'react';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../../i18next';
import { isValidUrl } from '../../../../../services/url';
import { TaskBuilderContext } from './types';

export type TaskBuilderBlockValidationStateItem = {
  error: boolean;
  fields: {
    [name: string]: string | null | undefined;
  };
};

export type TaskBuilderBlockValidationState = {
  [blockId: string]: TaskBuilderBlockValidationStateItem;
};

export const hasValidBlockState = (s: TaskBuilderBlockValidationState) => {
  return !Object.values(s).some((b) => b.error);
};

export const updateBlockFieldValidation = (
  state: TaskBuilderBlockValidationState,
  blockId: string,
  fieldName: string,
  reason: string | null | undefined,
  propagateInvalidBlockState: boolean = true
): TaskBuilderBlockValidationState => {
  const nextFields = {
    ...(state[blockId]?.fields || EMPTY_OBJ),
    [fieldName]: reason
  };
  const isBlockValid = !compact(Object.values(nextFields)).length;
  return {
    ...state,
    [blockId]: {
      ...(state[blockId] || EMPTY_OBJ),
      fields: nextFields,
      error: isBlockValid
        ? false
        : propagateInvalidBlockState
        ? true
        : state[blockId]?.error || false
    }
  };
};

export const updateBlockValidation = (
  state: TaskBuilderBlockValidationState,
  blockId: string
) => {
  const blockState = state[blockId];
  if (!blockState) {
    return state;
  }
  return {
    ...state,
    [blockId]: {
      ...blockState,
      error: !!compact(Object.values(blockState.fields)).length
    }
  };
};

type ValidateFn<T> = (
  b: T,
  t: UnsafeTFunction,
  expTpl: TaskBuilderContext,
  task?: ITaskTemplate
) => string | null | undefined;

type Validator<T extends TaskBlockTemplate> = {
  [field: string]: ValidateFn<T>;
};
const HOOKS_VALIDATOR: ValidateFn<SubTaskTemplate> = (b, t, expTpl) => {
  // `b.hooks` can be undefined on blocks authored before hooks were
  // mandatory on the BE shape (e.g., older AI-generated tasks). The
  // original `b.hooks.onComplete?.forEach` only guarded `onComplete`, not
  // `hooks` itself, so a missing `hooks` field threw `TypeError: Cannot
  // read properties of undefined (reading 'onComplete')`. That throw
  // propagated up through validateBlock → validateBlocks →
  // validateEntireTemplate, killing `onCheckAndLaunch` silently (the
  // callback is async with no try/catch). Symptom: "save" indicator
  // updated but no errors UI, no status transition, no launch.
  const hookStorageIssues: any[][] = [[]];
  b?.hooks?.onComplete?.forEach((h, i) => {
    h?.storages?.forEach((s, j) => {
      if (!s) return;
      hookStorageIssues[i] = hookStorageIssues[i] || [];
      hookStorageIssues[i][j] = validateDataDestination(s, t, expTpl, (st) =>
        st.destinationType === DestinatioType.COMPLETION_HOOK &&
        !st.completionText
          ? { completionText: t('Please enter a completion text') }
          : null
      );
    });
  });
  return compact(flatten(hookStorageIssues)).length
    ? JSON.stringify(hookStorageIssues)
    : null;
};
export const isReplacementToken = (x: string | null | undefined) => {
  // Validators pass values straight from the template into here; if the
  // field is missing or non-string we must not crash. `.match` on
  // undefined / number / object throws.
  if (typeof x !== 'string') return false;
  return !!x.match(/^\{\{.+\}\}$/);
};

// `b.data?.X` everywhere, not `b.data.X` — a missing `data` field on
// any block (legacy templates, AI partials, Firestore manual edits)
// would otherwise crash the per-field validator on the first
// dereference. Even though the outer try/catch in `validateBlock`
// catches the throw, the optional chain lets the validator return its
// specific "Please add X" message instead of falling through to the
// generic structural fallback.
//
// `hasItems` checks Array.isArray + non-empty. `?.length` alone is
// unsafe because strings also have `.length` — a Firestore field
// accidentally rewritten as `"foo"` instead of `[...]` would pass
// "non-empty" checks even though it's the wrong shape entirely.
const hasItems = (v: unknown): boolean => Array.isArray(v) && v.length > 0;

export const VALIDATORS_TEXT: Validator<IContentTextTemplate> = {
  text: (b, t) => {
    if (!b.data?.t?.t) {
      return t('Text required');
    }
  }
};

export const VALIDATORS_CALLOUT: Validator<IContentCalloutTemplate> = {
  text: (b, t) => {
    if (!b.data?.t?.t) {
      return t('Text required');
    }
  }
};

export const VALIDATORS_COLUMNS_LAYOUT: Validator<LayoutBlock> = {
  todo: (b, t) => {
    return undefined;
  }
};

export const VALIDATORS_HEADING: Validator<IContentHeadingTemplate> = {
  heading: (b, t) => {
    if (!b.data?.t) {
      return t('Heading required');
    }
  }
};

export const VALIDATORS_IMAGE: Validator<IContentImageTemplate> = {
  url: (b, t) => {
    const src = b.data?.src;
    if (!src) {
      return t('Image URL required');
    }

    if (isReplacementToken(src)) {
      return;
    }
    if (!isValidUrl(src)) {
      return t('Please enter a valid URL or choose a token');
    }
  }
};

export const VALIDATORS_DIVIDER: Validator<IContentDividerTemplate> = {
  divider: (b, t) => {
    if (!b.data?.variant) {
      return t('Variant required');
    }
  }
};

export const VALIDATORS_CALENDLY_INVITE: Validator<ISubTaskCalendlyInviteTemplate> = {
  event: (b, t) => {
    if (!b.data?.event) {
      return t('Please select an event');
    }
  }
};

// AI-generated CREATE_MEETING blocks have arrived in the wild with `data`
// containing only `title` — `pplToAdd`, `anchor`, `createLinksFor`,
// `description`, `at`, `repeat` all missing. Reading `.length` / `.type`
// on undefined crashed validateBlocks → validateEntireTemplate →
// onCheckAndLaunch silently (same pattern as the SUBMIT / HOOKS / QUESTION
// crashes). Optional-chain every dereference here so the validator
// surfaces user-actionable errors instead of throwing. The heal-on-launch
// normalizer (normalizeCreateMeetingBlock in templateValidation.ts) also
// backfills the missing fields so the persisted shape converges.
export const VALIDATORS_CREATE_CALENDAR_EVENT: Validator<ISubtaskCreateMeetingTemplate> = {
  pplToAdd: (b, t) => {
    if (!hasItems(b.data?.pplToAdd)) {
      return t('Please select who should be added to the event');
    }
  },
  title: (b, t) => {
    if (!b.data?.title) {
      return t('Please enter an event title');
    }
  },
  description: (b, t) => {
    if (!b.data?.description) {
      return t('Please give event a description');
    }
  },
  anchor: (b, t) => {
    if (!b.data?.anchor?.type) {
      return t('Please choose a relative anchor for your event');
    }
  },
  createLinksFor: (b, t) => {
    if (!hasItems(b.data?.createLinksFor)) {
      return t('Please select which calendars should be used');
    }
  },
  hooks: HOOKS_VALIDATOR
};

export const VALIDATORS_CONFIRM: Validator<ISubTaskConfirmTemplate> = {
  label: (b, t) => {
    if (!b.data?.label) {
      return t('Please give button a label');
    }
  },
  hooks: HOOKS_VALIDATOR
};

const ILLEGAL_COLUMN_NAMES = ['True', 'true', 'False', 'false'];

export const validateGoogleSheetStorage = (
  s: SubTaskDataDestinationGoogleSheet,
  t: UnsafeTFunction
) => {
  const errors: { [key: string]: string } = {};
  if (!s.columnName) {
    errors.columnName = t('Please define a column name');
  }
  if (ILLEGAL_COLUMN_NAMES.includes(s.columnName)) {
    errors.columnName = `Column name "${s.columnName}" not allowed`;
  }
  return Object.keys(errors).length ? errors : null;
};

export const validateExcelSheetStorage = (
  s: SubTaskDataDestinationExcel,
  t: UnsafeTFunction
) => {
  const errors: { [key: string]: string } = {};
  if (!s.columnName) {
    errors.columnName = t('Please define a column name');
  }
  if (ILLEGAL_COLUMN_NAMES.includes(s.columnName)) {
    errors.columnName = `Column name "${s.columnName}" not allowed`;
  }
  return Object.keys(errors).length ? errors : null;
};

export const validateUserProfileStorage = (
  s: SubTaskDataDestinationUserProfile,
  t: UnsafeTFunction
) => {
  const errors: { [key: string]: string } = {};
  if (!s.field) {
    errors.field = t('Please select the field to save answers to');
  }
  return Object.keys(errors).length ? errors : null;
};

export const validateBambooHrStorage = (
  s: SubTaskDataDestinationBambooHr,
  t: UnsafeTFunction
) => {
  const errors: { [key: string]: string } = {};
  if (!s.field) {
    errors.field = t('Please select the field to save answers to');
  }
  return Object.keys(errors).length ? errors : null;
};

export const validateStorage = (
  storage: SubTaskDataDestination,
  t: UnsafeTFunction,
  expTpl: TaskBuilderContext
) => {
  if (!storage || typeof storage !== 'object') return null;
  switch (storage.type) {
    case 'GOOGLE_SHEET':
      return validateGoogleSheetStorage(storage, t);
    case 'BAMBOO_HR':
      return validateBambooHrStorage(storage, t);
    case 'USER_PROFILE':
      return validateUserProfileStorage(storage, t);
    case 'MS_EXCEL':
      return validateExcelSheetStorage(storage, t);
    default:
      // ideally never happens
      console.warn('UNKNOWN STORAGE TYPE', (storage as any).type);
      return null;
  }
};

type StorageErrors = { [key: string]: string };

/**
 * Per-destination validation: the type-based `validateStorage` checks plus any
 * subtask-specific required-field checks supplied via `extra` (e.g. BG-check's
 * `source`, completion hooks' `completionText`). Returns a merged error map, or
 * null when the destination is fully valid.
 */
export const validateDataDestination = (
  s: SubTaskDataDestination,
  t: UnsafeTFunction,
  expTpl: TaskBuilderContext,
  extra?: (s: SubTaskDataDestination) => StorageErrors | null | undefined
): StorageErrors | null => {
  const errors = {
    ...(validateStorage(s, t, expTpl) || {}),
    ...(extra?.(s) || {})
  };
  return Object.keys(errors).length ? errors : null;
};

/**
 * Validates a flat list of data destinations and serializes the result into the
 * exact shape <StorageDestinations> reads back from `validation.fields.storage`:
 * a JSON array with a per-index error map (null where that destination is
 * valid), or null when every destination passes. Shared by every subtask that
 * renders <StorageDestinations> so the walk/serialize contract lives in one
 * place.
 *
 * Defensive null-guard (same family of bugs as the SUBMIT.email / HOOKS throws,
 * commit 6a596df): a missing or wrong-shape `storages` on legacy / AI-shaped
 * blocks would otherwise throw inside .forEach and silently kill
 * onCheckAndLaunch.
 */
export const validateStorages = (
  storages: SubTaskDataDestination[] | undefined,
  t: UnsafeTFunction,
  expTpl: TaskBuilderContext,
  extra?: (s: SubTaskDataDestination) => StorageErrors | null | undefined
): string | null => {
  if (!Array.isArray(storages)) return null;
  const issues: (StorageErrors | null)[] = [];
  storages.forEach((s, i) => {
    if (!s || typeof s !== 'object') return;
    issues[i] = validateDataDestination(s, t, expTpl, extra);
  });
  return compact(issues).length ? JSON.stringify(issues) : null;
};

export const VALIDATORS_QUESTION: Validator<ISubTaskQuestionTemplate> = {
  question: (b, t) => {
    // `b.data.question` is missing on malformed QUESTION blocks (the
    // previous AI shape `data: { title, type, options }` doesn't nest
    // into `question`). Surface a clear error instead of throwing.
    const q = b.data?.question;
    if (!q) {
      return t('Please choose a question type');
    }
    if (q.type !== 'address' && !q.label) {
      return t('Please enter a question');
    }
  },
  answerOptions: (b, t) => {
    const q = b.data?.question;
    if (!q || q.type !== 'select') {
      return null;
    }
    const options = Array.isArray(q.options) ? q.options : [];
    if (options.length < 2) {
      return t('Please choose at least 2 options');
    }
    if (new Set(options.map((o) => o?.label)).size < options.length) {
      return t('Please choose unique options');
    }
  },
  placeholder: (b, t) => {
    // Slack cannot display placeholders longer than that!
    const MAX_PLACEHOLDER_LENGTH = 150;
    const q = b.data?.question;
    if (
      q?.type === 'text' &&
      ((q as { placeholder?: string }).placeholder?.length ?? 0) > MAX_PLACEHOLDER_LENGTH
    ) {
      return t('Placeholders can be at most {{ maxLength }} characters long', {
        maxLength: MAX_PLACEHOLDER_LENGTH
      });
    }
  },
  // Defensive null-guard for the same family of bugs as the
  // SUBMIT.email crash above and the HOOKS_VALIDATOR throw (commit
  // 6a596df). Missing OR wrong-shape `storages` on legacy blocks
  // would otherwise throw inside .forEach and silently kill
  // onCheckAndLaunch.
  scale: (b, t) => {
    const q = b.data?.question;
    if (!q || !isScaleQuestion(q)) {
      return null;
    }
    const err = validateScaleQuestion(q);
    if (err === 'LABEL_TOO_LONG') {
      return t('Scale labels can be at most {{ maxLength }} characters long', {
        maxLength: SCALE_ENDPOINT_LABEL_MAX_LENGTH
      });
    }
    if (err) {
      return t('Please choose a valid scale range');
    }
    return null;
  },
  storage: (b, t, expTpl) => validateStorages(b.data?.storages, t, expTpl)
};

export const VALIDATORS_SUBMIT: Validator<ISubTaskSubmitTemplate> = {
  email: (b, t) => {
    // AI-created SUBMIT subtasks land with `data: { title }` only — no
    // `to` field yet. Reading `b.data.to.type` directly crashed
    // validateBlocks → validateEntireTemplate → onCheckAndLaunch with a
    // silent promise rejection (see also HOOKS_VALIDATOR fix). Defer to
    // the `target` validator below, which already handles missing `to`
    // with a proper user-facing message — no double-report.
    const to = b.data?.to;
    if (!to) {
      return null;
    }
    if (to.type !== 'EMAIL') {
      return null;
    }
    if (!to.email) {
      return t('Please enter an email address');
    }
    if (isReplacementToken(to.email)) {
      return null;
    }
    if (!isValidEmail(to.email)) {
      return t('Please enter a valid email address');
    }
  },
  // For non-EMAIL delivery targets (Google Drive, OneDrive, SharePoint) the
  // resourceId points at the destination folder/site. Without it, the upload
  // has nowhere to land at runtime.
  target: (b, t) => {
    const to = b.data?.to;
    if (!to) {
      return t('Please select a delivery destination');
    }
    if (to.type === 'EMAIL') {
      return null; // EMAIL branch is handled by the 'email' validator above
    }
    if (!to.resourceId) {
      return t('Please choose a destination for the uploaded file');
    }
  },
  fileNameTemplate: (b, t) => {
    if (!b.data?.fileNameFormat) {
      return t('Please enter a template');
    }
    // TODO - make sure that there is at least one participant related variable in here - otherwise filenames are not unique
  },
  hooks: HOOKS_VALIDATOR
};

export const VALIDATORS_VISIT: Validator<ISubTaskVisitTemplate> = {
  url: (b, t) => {
    const url = b.data?.url;
    if (!url) {
      return t('Link URL required');
    }
    if (isReplacementToken(url)) {
      return;
    }
    if (!isValidUrl(url)) {
      return t('Please enter a valid URL or choose a token');
    }
  },
  hooks: HOOKS_VALIDATOR
};

export const VALIDATORS_WATCH: Validator<ISubTaskWatchTemplate> = {
  url: (b, t) => {
    const url = b.data?.url;
    if (!url) {
      return t('Watch URL required');
    }
    if (!isValidUrl(url)) {
      return t('Please enter a valid URL');
    }
  },
  hooks: HOOKS_VALIDATOR
};

export const VALIDATORS_DOCUMENT_READ: Validator<ISubTaskDocumentReadTemplate> = {
  title: (b, t) => {
    if (!b.data?.title) {
      return t('Please choose a document title');
    }
  },
  file: (b) => {
    if (!b.data?.docUrl) {
      return 'Please choose a valid file';
    }
  },
  hooks: HOOKS_VALIDATOR
};

export const VALIDATORS_SWAG_UP_CHOICE: Validator<ISubTaskSwagUpChoiceTemplate> = {
  choices: (b, t) => {
    if (!hasItems(b.data?.choices)) {
      return t('Please select some swag to choose from');
    }
  },
  contacts: (b, t) => {
    if (!hasItems(b.data?.swagUpContactEmails)) {
      return t(
        'Please select at least one contact we can reach out to in case something goes wrong'
      );
    }
  }
};

export const VALIDATORS_DATA_FIELD_CHANGE: Validator<ISubtaskDataFieldChangeTemplate> = {
  instructions: (b, t) => {
    if (!b.data?.instructions?.t) {
      return t('Please write some instructions');
    }
  }
  // rules: (b, t) => {
  //   if (!isEmptyRule(b.data.rules)) {
  //     return t('Please define a completion criterion for the subtask');
  //   }
  // }
};

export const VALIDATORS_DOCUSIGN_ENVELOPE_SIGN: Validator<ISubTaskDocuSignEnvelopeSignTemplate> = {
  // TODO DocuSign
};

export const VALIDATORS_COMPLETE_LMS_MODULE: Validator<ISubTaskCompleteLmsModuleTemplate> = {
  unit: (b, t) => {
    if (!b.data?.unit?.id) {
      return t('A course is required');
    }
  },
  supportContact: (b, t) => {
    // Optional-chain the whole path — legacy / AI-shaped blocks may lack
    // `data` or `supportContact` entirely.
    const email = b.data?.supportContact?.email?.trim();
    if (!email || !isValidEmail(email)) {
      return t('Please provide a valid support contact email');
    }
  }
};

export const VALIDATORS_BACKGROUND_CHECK: Validator<ISubTaskBackgroundCheckInviteTemplate> = {
  linkText: (b, t) => {
    if (!b.data?.linkText?.trim()) {
      return t('subTask.backgroundCheck.invite.linkText.required');
    }
  },
  package: (b, t) => {
    if (!b.data?.package) {
      return t('subTask.backgroundCheck.invite.package.required');
    }
  },
  workLocationSourceField: (b, t) => {
    if (!b.data?.workLocationSourceField?.trim()) {
      return t(
        'Select the user profile field that holds the working location (US state).'
      );
    }
  },
  // If a node was picked, its `packageSlugs` must include the picked package's
  // `slug`. Both objects live on the subtask itself, so this is a pure check.
  // A node with an empty `packageSlugs` list is treated as unrestricted — any
  // package is valid, so the membership check is skipped.
  nodePackage: (b, t) => {
    const data = b.data;
    if (!data) return;
    const { package: pkg, node } = data;
    if (!pkg || !node) return;
    const slugs = Array.isArray(node.packageSlugs) ? node.packageSlugs : [];
    if (slugs.length === 0) return;
    if (!slugs.includes(pkg.slug)) {
      return t('subTask.backgroundCheck.invite.package.notInNode');
    }
  },
  // Mirrors VALIDATORS_QUESTION.storage (same defensive null-guard for legacy /
  // AI-shaped blocks). Each BG-check data destination must pick a screening
  // result `source` AND have its storage-type fields filled in — an incomplete
  // destination must block the save instead of silently persisting.
  storage: (b, t, expTpl) =>
    validateStorages(b.data?.storages, t, expTpl, (s) =>
      s.source ? null : { source: t('Please select a check result') }
    )
};

export const resetBlockValidation = (
  blockId: string,
  setValidation: React.Dispatch<SetStateAction<TaskBuilderBlockValidationState>>
) => {
  setValidation((x) => omit(x, blockId));
};

export const validateBlock = (
  s: TaskBuilderBlockValidationState,
  b: TaskBlockTemplate,
  t: UnsafeTFunction,
  expTpl: TaskBuilderContext
) => {
  const validate = <T extends TaskBlockTemplate>(
    validator: Validator<T>,
    block: T
  ) => {
    return Object.entries(validator).reduce((m, [field, v]) => {
      // Universal safety net. Individual validators above guard their
      // known crash sites with optional chaining, but new field shapes
      // (or never-seen AI shapes) will keep finding ways to throw.
      // The validator is the bend-back-into-shape boundary — it MUST
      // NOT propagate a TypeError up through validateBlocks →
      // validateEntireTemplate → onCheckAndLaunch, which would silently
      // kill Check & Launch with no UI feedback. Catch here, log for
      // dev triage, and surface a generic user-actionable error so the
      // admin at least sees that this block needs attention.
      let result: string | null | undefined;
      try {
        result = v(block, t, expTpl);
      } catch (err) {
        console.warn(
          `[validateBlock] validator threw for ${b.type}.${field} on block ${b.id}; treating as structural error`,
          { err, block }
        );
        result = t(
          'This block has invalid configuration. Open it to finish the setup, or remove and re-add it.'
        );
      }
      return updateBlockFieldValidation(m, b.id, field, result);
    }, s);
  };

  // Outer guard: if `b` is structurally bogus (null, missing `type`, or
  // a type we don't recognize), the switch's `default: assertNever(b)`
  // would throw. validateBlock is called directly from TaskBuilder
  // (e.g., when a new subtask is being added with a partially-
  // initialized shape), so this throw would escape past the inner
  // validate() helper's try/catch and crash the caller. Wrap the whole
  // switch — return a single structural error tied to the block's id.
  if (!b || typeof b !== 'object') {
    console.warn('[validateBlock] received null/non-object block', { b });
    return s;
  }
  try {
    switch (b.type) {
      case ContentType.HEADING:
        return validate(VALIDATORS_HEADING, b);
      case ContentType.TEXT:
        return validate(VALIDATORS_TEXT, b);
      case ContentType.CALLOUT:
        return validate(VALIDATORS_CALLOUT, b);

      case ContentType.IMAGE:
        return validate(VALIDATORS_IMAGE, b);
      case ContentType.DIVIDER:
        return validate(VALIDATORS_DIVIDER, b);

      case SubTaskType.CALENDLY_INVITE:
        return validate(VALIDATORS_CALENDLY_INVITE, b);
      case SubTaskType.CONFIRM:
        return validate(VALIDATORS_CONFIRM, b);
      case SubTaskType.MEET:
        return s;
      case SubTaskType.CREATE_MEETING:
        return validate(VALIDATORS_CREATE_CALENDAR_EVENT, b);
      case SubTaskType.QUESTION:
        return validate(VALIDATORS_QUESTION, b);
      case SubTaskType.SUBMIT:
        return validate(VALIDATORS_SUBMIT, b);
      case SubTaskType.VISIT:
        return validate(VALIDATORS_VISIT, b);
      case SubTaskType.WATCH:
        return validate(VALIDATORS_WATCH, b);
      case SubTaskType.DOCUMENT_READ:
        return validate(VALIDATORS_DOCUMENT_READ, b);
      case SubTaskType.SWAG_UP_CHOICE:
        return validate(VALIDATORS_SWAG_UP_CHOICE, b);
      case SubTaskType.DATA_FIELD_CHANGE:
        return validate(VALIDATORS_DATA_FIELD_CHANGE, b);
      case SubTaskType.DOCUSIGN_ENVELOPE_SIGN:
        return validate(VALIDATORS_DOCUSIGN_ENVELOPE_SIGN, b);
      case SubTaskType.BACKGROUND_CHECK_INVITE:
        return validate(VALIDATORS_BACKGROUND_CHECK, b);
      case SubTaskType.COMPLETE_LMS_MODULE:
        return validate(VALIDATORS_COMPLETE_LMS_MODULE, b);
      case LayoutBlockType.COLUMNS:
        return validate(VALIDATORS_COLUMNS_LAYOUT, b);
      default:
        return assertNever(b);
    }
  } catch (err) {
    console.warn(
      `[validateBlock] unhandled throw or unknown block type (type=${(b as { type?: string }).type}, id=${(b as { id?: string }).id})`,
      err
    );
    return updateBlockFieldValidation(
      s,
      (b as { id?: string }).id ?? '<unknown>',
      'structure',
      t(
        'This block has an unknown or invalid type. Remove it and re-add the block from the picker.'
      )
    );
  }
};

export const validateBlocks = (
  bs: TaskBlockTemplate[] | undefined,
  t: UnsafeTFunction,
  expTpl: TaskBuilderContext
) => {
  // `bs` can be undefined OR the wrong shape (string/object) when a
  // write or Firestore edit corrupts the task. Coerce non-arrays to
  // empty before .reduce — otherwise the call would crash.
  const safe = Array.isArray(bs) ? bs : [];
  return safe.reduce((m, b) => validateBlock(m, b, t, expTpl), {});
};

export const useFieldValidator = <T extends TaskBlockTemplate>(
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >,
  validator: Validator<T>,
  field: string
) => {
  const { t } = useUnsafeTranslation();
  return useCallback(
    (block: T, expTpl: TaskBuilderContext) => {
      setValidation((s) =>
        updateBlockFieldValidation(
          s,
          block.id,
          field,
          validator[field](block, t, expTpl)
        )
      );
    },
    [setValidation, validator, field, t]
  );
};
